/**
 * Self-running guided product tour ("Ver demostración"). A step timeline walks
 * the whole app, spotlights individual components with value-selling coachmarks,
 * triggers a few live moments (AI assistant answers, validations run), and loops.
 *
 * Additive and flag-free — nothing runs unless the user presses play. Components
 * opt into "live actions" by reading `useDemo().action`; spotlight targets are
 * marked with `data-tour="<id>"`.
 */
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, X } from 'lucide-react';

export type DemoAction = 'openAssistant' | 'runValidations' | 'showRiskTab';

interface TourStep {
  key?: string;
  route?: string;
  target?: string; // data-tour id to spotlight; omit → centered card
  title?: string;
  body: string;
  duration?: number; // ms; ignored when selfRun
  before?: DemoAction; // a live action a component performs
  selfRun?: boolean; // intake scene — waits for sceneDone() instead of a timer
}

const D = 5800; // default dwell per spotlight (slower, sales-friendly)

const STEPS: TourStep[] = [
  // --- Bienvenida ---
  { route: '/inicio', body: 'Te muestro Cumpli en un minuto — cumplimiento de la Ley 23, de punta a punta.', title: 'Bienvenido a Cumpli', duration: 4200 },
  { route: '/inicio', target: 'home-cta', title: 'Registrar un cliente', body: 'El corazón del día a día. Onboarda un cliente en minutos, no en horas.', duration: 5200 },
  { route: '/inicio', target: 'home-stats', title: 'Tu operación de un vistazo', body: 'Cuántos clientes tienes, cuántos están listos y cuántos siguen pendientes.', duration: 5000 },
  { route: '/inicio', target: 'home-recordatorios', title: 'Nunca se te pasa nada', body: 'Recordatorios automáticos de documentos por vencer — sin depender de calendarios externos.', duration: 5200 },
  { route: '/inicio', target: 'home-novedades', title: 'Al día con la regulación', body: 'Novedades de la SBP, la UAF y el GAFI — sin salir de la app.', duration: 5200 },
  // --- Funciones transversales del encabezado ---
  { route: '/inicio', target: 'search-palette-btn', title: 'Búsqueda global', body: 'Encuentra cualquier cliente por nombre o cédula en segundos — desde cualquier pantalla, sin navegar menú a menú.', duration: 5200 },
  { route: '/inicio', target: 'notifications', title: 'Notificaciones en tiempo real', body: 'Documentos por vencer y alertas de monitoreo llegan aquí — el equipo siempre sabe qué requiere atención inmediata.', duration: 5000 },
  { route: '/inicio', target: 'assistant-panel', before: 'openAssistant', title: 'Asistente de IA con memoria', body: 'Resume el riesgo de un cliente, redacta borradores de ROS y recuerda el contexto de la conversación — sin perder el hilo entre consultas.', duration: 7000 },
  { route: '/inicio', target: 'registro-nav', title: 'Todo en una sola consola', body: 'Registro para dar de alta clientes, y el resto de la barra lateral para revisar y decidir. Un solo lugar para todo el flujo de la Ley 23 — sin cambiar de app.', duration: 5200 },
  // --- Flujo de Registro ---
  { key: 'intake', route: '/expedientes/nuevo', selfRun: true, title: 'Registro guiado', body: 'Una sola pregunta a la vez — diseñado para que cualquier persona lo complete sin errores.' },
  { route: '/expedientes', target: 'client-card', title: 'Tus clientes', body: 'En lenguaje claro: “Listo”, “Falta información”, “En revisión”. Sin jerga regulatoria.', duration: 5200 },
  { route: '/expedientes/EXP-2026-0042', target: 'detail-checklist', title: '¿Qué falta?', body: 'Siempre sabes exactamente qué documento falta para completar el expediente — sin adivinar.', duration: 5400 },
  // --- Cumplimiento: el Agente es la puerta de entrada ---
  { route: '/agente', target: 'agente-nav', title: 'Nuevo: el Agente', body: 'Pregunta o pide acciones en lenguaje natural — el asistente propone y tú confirmas antes de ejecutar. La misma conversación del ⌘K, ahora en pantalla completa: la puerta de entrada a Cumplimiento.', duration: 6500 },
  // --- Panel de Cumplimiento ---
  { route: '/panel', target: 'panel-kpis', title: 'Panel de Cumplimiento', body: 'El equipo de cumplimiento ve todo el panorama del tenant en tiempo real — onboardings, riesgos y alertas activas.', duration: 5400 },
  { route: '/panel', target: 'panel-risk', title: 'Distribución de riesgo', body: 'Cuántos clientes son de riesgo alto, medio o bajo — al instante, sin reportes manuales.', duration: 5000 },
  { route: '/panel', target: 'panel-chart', title: 'Tendencia de onboarding', body: 'La actividad semana a semana, con detalle al pasar el cursor. Identifica picos y caídas sin exportar a Excel.', duration: 5000 },
  { route: '/panel', title: 'Aislamiento total entre entidades', body: 'Cada entidad regulada opera en su propio espacio. Datos, usuarios y bitácoras completamente aislados: ningún auditor accede al tenant de otra entidad.', duration: 5400 },
  // --- Cola de revisión y análisis ---
  { route: '/revision', target: 'revision-table', title: 'Cola de revisión', body: 'Los analistas trabajan los expedientes priorizados por estado, nivel de riesgo y próxima fecha de revisión.', duration: 5400 },
  { route: '/revision/EXP-2026-0042', target: 'rev-validations', before: 'runValidations', title: 'Validaciones automáticas', body: 'Identidad (SVI), crédito (APC), listas de sanciones y PEP — corren solas, en segundos, sin intervención manual.', duration: 7500 },
  { route: '/revision/EXP-2026-0042', target: 'rev-risk', before: 'showRiskTab', title: 'Riesgo ponderado', body: 'El riesgo se calcula con factores ponderados configurables y define la cadencia de revisión periódica.', duration: 5600 },
  { route: '/revision/EXP-2026-0042', target: 'rev-decision', title: 'Bitácora de auditoría inmutable', body: 'Aprobar, escalar o rechazar — cada decisión queda en una bitácora que no puede alterarse. Lista para cualquier auditoría de la SBP y la UAF desde el primer día.', duration: 5800 },
  // --- Alertas y casos ---
  { route: '/alertas', target: 'alertas-table', title: 'Alertas y casos', body: 'El monitoreo continuo y el re-screening generan alertas automáticamente — sin depender de revisiones manuales.', duration: 5400 },
  { route: '/alertas/ALT-1188', target: 'alerta-lifecycle', title: 'Ciclo de vida del caso', body: 'Desde que el motor genera la alerta hasta que el analista la resuelve: cada paso queda en la bitácora para el regulador.', duration: 5600 },
  // --- Superficies operativas de Cumplimiento ---
  { route: '/documentos', target: 'docs-table', title: 'Gestión documental', body: 'Todos los documentos del tenant en un solo lugar — recepción, vigencia y alertas por vencimiento automáticas.', duration: 5400 },
  { route: '/alertas?tab=validacion-masiva', target: 'screening-form', title: 'Validación masiva', body: 'Pegue una lista de nombres y descubra en segundos cuáles ya pertenecen a su cartera de clientes — ideal para cotejar una nueva lista restrictiva contra su base.', duration: 5400 },
  { route: '/matriz-riesgo', target: 'risk-matrix-weights', title: 'Matriz de riesgo configurable', body: 'Pondere los factores AML/CFT según su modelo de negocio. El motor recalcula el nivel de riesgo de cada cliente al guardar la matriz.', duration: 5400 },
  { route: '/usuarios', target: 'users-table', title: 'Control de acceso por roles', body: 'Asigne quién puede registrar, quién revisa y quién decide. El RBAC garantiza que nadie ve más de lo que le corresponde.', duration: 5400 },
  // --- Cierre ---
  { route: '/inicio', title: 'Desde el teléfono también', body: 'La consola funciona en cualquier dispositivo. Instálela como app en iOS y Android — sin descarga en tienda de aplicaciones.', duration: 5000 },
  { route: '/inicio', title: 'Cumpli — cumplimiento de punta a punta', body: 'De la recepción al reporte a la UAF, en una sola plataforma. ¿Lo volvemos a ver?', duration: 6000 },
];

interface DemoCtx {
  playing: boolean;
  sceneKey: string | null;
  action: DemoAction | null;
  start: () => void;
  stop: () => void;
  sceneDone: () => void;
}

const Ctx = createContext<DemoCtx | null>(null);

export function useDemo(): DemoCtx {
  const v = useContext(Ctx);
  if (!v) throw new Error('useDemo must be used within DemoProvider');
  return v;
}

export function DemoProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [playing, setPlaying] = useState(false);
  const [i, setI] = useState(0);

  const advance = useCallback(() => setI((n) => (n + 1) % STEPS.length), []);
  const start = useCallback(() => {
    setI(0);
    setPlaying(true);
    navigate('/inicio');
  }, [navigate]);
  const stop = useCallback(() => {
    setPlaying(false);
    setI(0);
    navigate('/agente');
  }, [navigate]);

  useEffect(() => {
    if (!playing) return;
    const step = STEPS[i]!;
    if (step.route) navigate(step.route);
    if (step.selfRun) return; // intake drives itself; advances via sceneDone()
    const t = setTimeout(advance, step.duration ?? D);
    return () => clearTimeout(t);
  }, [playing, i, navigate, advance]);

  const value = useMemo<DemoCtx>(
    () => ({
      playing,
      sceneKey: playing ? STEPS[i]!.key ?? null : null,
      action: playing ? STEPS[i]!.before ?? null : null,
      start,
      stop,
      sceneDone: advance,
    }),
    [playing, i, start, stop, advance]
  );

  return (
    <Ctx.Provider value={value}>
      {children}
      {playing && <TourSpotlight step={STEPS[i]!} index={i} total={STEPS.length} onExit={stop} />}
    </Ctx.Provider>
  );
}

/** Dimmed cut-out over the target + a value-selling coachmark card. */
function TourSpotlight({
  step,
  index,
  total,
  onExit,
}: {
  step: TourStep;
  index: number;
  total: number;
  onExit: () => void;
}) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [isMobile, setIsMobile] = useState(() => (typeof window !== 'undefined' ? window.innerWidth < 640 : false));

  useEffect(() => {
    const onResize = () => setIsMobile(window.innerWidth < 640);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  useEffect(() => {
    setRect(null);
    if (!step.target) return;
    let raf = 0;
    let tries = 0;
    const measure = () => {
      const el = document.querySelector(`[data-tour="${step.target}"]`);
      if (el) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        // let the smooth scroll settle, then lock the rect
        setTimeout(() => setRect(el.getBoundingClientRect()), 320);
        return;
      }
      if (tries++ < 80) raf = requestAnimationFrame(measure); // wait for live actions to render it
    };
    measure();
    const onScroll = () => {
      const el = document.querySelector(`[data-tour="${step.target}"]`);
      if (el) setRect(el.getBoundingClientRect());
    };
    window.addEventListener('scroll', onScroll, true);
    window.addEventListener('resize', onScroll);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('scroll', onScroll, true);
      window.removeEventListener('resize', onScroll);
    };
  }, [step.target, index]);

  // Wider horizontal pad so bleeding rails (-mx-4/-mx-6) are fully framed.
  const padX = 22;
  const padY = 12;
  const hole = rect
    ? {
        top: rect.top - padY,
        left: Math.max(4, rect.left - padX),
        width: Math.min(rect.width + padX * 2, window.innerWidth - 8),
        height: rect.height + padY * 2,
      }
    : null;

  // Coachmark position: bottom sheet on mobile / no target (lifted to clear bottom
  // buttons); near the target on desktop.
  const cardW = 380;
  let cardStyle: React.CSSProperties;
  if (!hole) {
    cardStyle = { left: '50%', transform: 'translateX(-50%)', bottom: 96, maxWidth: 'calc(100vw - 32px)', width: cardW };
  } else if (isMobile) {
    cardStyle = { left: '50%', transform: 'translateX(-50%)', bottom: 24, maxWidth: 'calc(100vw - 32px)', width: cardW };
  } else {
    const below = hole.top + hole.height + 14;
    const placeBelow = below + 170 < window.innerHeight;
    const top = placeBelow ? below : Math.max(16, hole.top - 184);
    const left = Math.min(Math.max(16, hole.left + hole.width / 2 - cardW / 2), window.innerWidth - cardW - 16);
    cardStyle = { top, left, width: cardW };
  }

  return (
    <div className="pointer-events-none fixed inset-0 z-[80]">
      {/* dimmed backdrop with a cut-out — only for targeted steps, so self-running
          screens (intake, welcome, closing) stay fully visible. */}
      {hole && (
        <div
          className="absolute rounded-2xl ring-2 ring-primary transition-all duration-300"
          style={{ ...hole, boxShadow: '0 0 0 9999px rgba(25,33,28,.55)' }}
        />
      )}

      {/* coachmark */}
      <div
        className="pointer-events-auto fixed animate-[pop_.35s_ease-out] rounded-2xl border border-line bg-panel p-4 shadow-pop"
        style={cardStyle}
      >
        <div className="mb-1.5 flex items-center gap-2">
          <span className="relative grid h-5 w-5 place-items-center">
            <span className="absolute h-2.5 w-2.5 animate-ping rounded-full bg-primary/70" />
            <span className="h-2 w-2 rounded-full bg-primary" />
          </span>
          {step.title && <span className="font-serif text-base font-medium text-ink">{step.title}</span>}
          <button
            onClick={onExit}
            className="ml-auto flex items-center gap-1 rounded-lg px-2 py-1 text-xs font-bold text-ink-soft hover:bg-canvas"
          >
            <X className="h-3.5 w-3.5" strokeWidth={2.5} /> Salir
          </button>
        </div>
        <p className="text-sm leading-snug text-ink-soft">{step.body}</p>
        <div className="mt-3 flex items-center gap-2">
          <div className="h-1 flex-1 overflow-hidden rounded-full bg-line">
            <div className="h-full rounded-full bg-primary transition-[width] duration-500" style={{ width: `${((index + 1) / total) * 100}%` }} />
          </div>
          <span className="font-mono text-[10px] text-ink-faint">{index + 1}/{total}</span>
        </div>
      </div>
    </div>
  );
}

/** Back-compat: App.tsx renders <DemoBar/>; the overlay now lives in the provider. */
export function DemoBar() {
  return null;
}

/** Entry button — starts the self-running tour. */
export function DemoPlayButton({ className }: { className?: string }) {
  const { start } = useDemo();
  return (
    <button
      onClick={start}
      className={
        className ??
        'inline-flex items-center gap-2 rounded-2xl border border-white/30 px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-white/10'
      }
    >
      <Play className="h-4 w-4 fill-current" strokeWidth={0} /> Ver demostración
    </button>
  );
}
